import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { ArrowLeft } from 'lucide-react';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { useCpdAutofill, MAX_CERTIFICATE_PAGES } from '../components/workflow/CpdAutofill';
import CpdCertificateRows, {
  useCpdRows, cpdRowError, cpdRowsGaps, describeCpdRows,
} from '../components/workflow/CpdCertificateRows';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';

// External CPD declaration — submission form (docs/modules/workflow-ld-cpd.md).
// The declaration is always for the logged-in user; the backend resolves the
// staff link from the login email (identity rule: email only), which is why
// this form has no staff picker — the only difference from HR's record modal
// in Management → Training Records.
//
// Read on upload, edit before submitting:
// choosing files reads them straight away and
// fills one editable row per certificate. The submitter fixes whatever the
// reader got wrong and types in whatever it could not read at all — a blurry
// photo or a Word certificate still gets declared, it just gets typed.
//
// ONE ROW = ONE DECLARATION. A PDF holding three certificates gives three rows
// and files three declarations, each with its own copy of the file. The rows
// themselves (and the rule for when they may be rebuilt) live in
// components/workflow/CpdCertificateRows — HR's record modal renders the very
// same list.
//
// What the reader saw is kept with each declaration (`extracted`) even when the
// submitter edits over it, so the "What the certificate says" card can put
// the two side by side.
//
// A declaration FILES ON SUBMISSION: the hours are on the training record as
// soon as this form succeeds. HR spot-checks the flagged ones afterwards.

const WorkflowCpdNew = () => {
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState([]);
  // The shared training-category list — every certificate row picks
  // its own. Active only: nothing new files under a retired category.
  const [categoryOptions, setCategoryOptions] = useState([]);
  useEffect(() => {
    fetchJSON('/api/ld/training-categories/options')
      .then((data) => setCategoryOptions(data.data || []))
      .catch(() => setCategoryOptions([]));
  }, []);
  // Post-submit outcome for a multi-certificate upload: the declarations
  // created, shown in place of the form (there is no single detail page for a
  // batch). A lone declaration goes straight to its own page instead.
  const [outcome, setOutcome] = useState(null);

  // The reader is the same one HR's record modal uses; `selfDeclaration` keeps
  // the "this certificate is issued to someone else" warning, which only makes
  // sense when you are declaring your OWN training.
  const noop = useCallback(() => {}, []);
  const autofill = useCpdAutofill({
    files,
    onExtracted: noop,        // rows are built from the hook's own results below
    multiple: true,
    selfDeclaration: true,
  });
  const { busy } = autofill;

  const [rows, setRows, generation] = useCpdRows({ files, autofill });
  // A fresh read is a fresh attempt — yesterday's red text does not belong on
  // rows that have just been rebuilt.
  useEffect(() => { setAttempted(false); }, [generation]);

  const pickFiles = (picked) => {
    setFiles(picked);
    setError(null);
    setOutcome(null);
  };

  const setRowField = (index, key, value) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)));

  // Dropping a row drops that certificate from the submission; a file left with
  // no row of its own is simply not filed (the server keeps only the files its
  // entries point at).
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  // Everything still empty, keyed to the control it belongs to: the upload
  // itself while there is no row, otherwise each row's own fields.
  const gaps = useMemo(() => (
    rows.length === 0
      ? [{ key: 'files', label: 'Upload Certificates' }]
      : cpdRowsGaps(rows)
  ), [rows]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    setAttempted(true);
    if (!validation.validate()) { setError(null); return; }
    // Empty fields are marked above; what is left for this check is the rule
    // no empty box can express (hours must be a positive number).
    const bad = rows.findIndex((r) => cpdRowError(r) !== null);
    if (bad !== -1) {
      setError(rows.length === 1
        ? cpdRowError(rows[bad])
        : `Certificate ${bad + 1} (${rows[bad].filename}): ${cpdRowError(rows[bad])}`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      form.set('entries', JSON.stringify(rows.map((r) => ({
        trainingTitle: r.trainingTitle.trim(),
        certificateDate: r.certificateDate,
        hours: r.hours,
        categoryId: r.categoryId,
        remarks: remarks.trim() || undefined,
        // Present → the declaration is marked as read from a certificate;
        // absent → typed by hand. The server decides `extractionSource` on it.
        extracted: r.extracted || undefined,
        fileIndexes: [r.fileIndex],
      }))));

      const data = await fetchJSON('/api/ld/cpd-requests', { method: 'POST', body: form });
      const created = data.requests || (data.request ? [data.request] : []);
      const turnedAway = data.rejected || [];
      // Straight to the declaration only when the whole upload became it.
      // Anything turned away has to be said out loud, or the submitter walks
      // away believing they declared two courses when they declared one.
      if (created.length === 1 && turnedAway.length === 0) {
        navigate(`/workflow/cpd/${created[0].id}`);
        return;
      }
      setOutcome({ created, rejected: turnedAway });
      setFiles([]);
      setRows([]);
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  if (outcome) {
    // Result view — same page skeleton as FormShell, without the submit row.
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <Link to="/workflow/cpd" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" /> External CPD</Link>
        <div className="mt-2 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Submission Result</h1>
          <p className="text-sm text-slate-500 mt-1">
            {(outcome.rejected || []).length > 0
              ? 'Each certificate is filed as its own declaration — here is what is on your training record and what is not.'
              : 'Each certificate was filed as its own declaration and is on your training record.'}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          {outcome.created.length > 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              <p className="font-medium">
                {outcome.created.length === 1
                  ? '1 declaration filed'
                  : `${outcome.created.length} declarations filed`}
              </p>
              <ul className="mt-2 space-y-1 text-xs">
                {outcome.created.map((r) => (
                  <li key={r.id}>
                    <RefLink to={`/workflow/cpd/${r.id}`} className="font-medium underline hover:no-underline">
                      {r.requestNo || `#${r.id}`}
                    </RefLink>
                    {' — '}{r.trainingTitle} ({r.hours}h, {formatDate(r.certificateDate)})
                    {(r.flags || []).length > 0 && (
                      <span className="ml-1 text-amber-700 font-medium">
                        — flagged for HR's spot-check
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Certificates the upload contained but nothing was filed for.
              Turned away by the duplicate guard, or simply unreadable — either
              way it belongs on the same screen as the successes, not in a
              console log. */}
          {(outcome.rejected || []).length > 0 && (
            <div className={`p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 ${
              outcome.created.length > 0 ? 'mt-3' : ''}`}>
              <p className="font-medium text-slate-700">
                {outcome.rejected.length === 1
                  ? '1 certificate was not filed'
                  : `${outcome.rejected.length} certificates were not filed`}
              </p>
              <ul className="mt-2 space-y-1 text-xs">
                {outcome.rejected.map((r, i) => (
                  <li key={`rejected-${i}`}>
                    <span className="font-medium">
                      {r.declaration?.trainingTitle
                        || [r.filename, r.certificate ? `certificate ${r.certificate}` : null]
                          .filter(Boolean).join(' — ')
                        || `Certificate ${i + 1}`}
                    </span>
                    {r.declaration && (
                      <> ({r.declaration.hours}h, {formatDate(r.declaration.certificateDate)})</>
                    )}
                    {' — '}{r.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setOutcome(null)}
              className="px-3 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
            >
              Submit More Certificates
            </button>
            <Link
              to="/workflow/cpd"
              className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              Back to My Declarations
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormShell
      backTo="/workflow/cpd"
      backLabel="External CPD"
      title="New External CPD Declaration"
      subtitle="Upload your certificates and check the details filled in."
      error={error || validation.error}
      onSubmit={submit}
      submitLabel={rows.length > 1 ? `Submit ${rows.length} Declarations` : 'Submit'}
      submitting={saving}
      submitDisabled={busy || rows.length === 0}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Certificates">
          <Field
            label="Upload Certificates"
            required
            field="files"
            action={autofill.button}
            hint={<>PDF / JPG / PNG / DOC, up to 30 MB. <InfoTip text={`PDF / JPG / PNG are read for you — a photo of the certificate is fine, and one PDF may hold several certificates (each becomes its own declaration). Word files, and anything the reader cannot make out, are simply typed in below. Up to ${MAX_CERTIFICATE_PAGES} pages per file, 10 files at a time.`} /></>}
          >
            <FilePicker
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              disabled={saving || busy}
              onChange={e => pickFiles(Array.from(e.target.files || []))}
              showSelection={false}
            />
          </Field>
          {autofill.panel}
        </FormSection>

        <FormSection
          step={2}
          title="Details"
          hint={rows.length > 1 ? `${describeCpdRows(rows)} — one declaration each` : undefined}
        >
          {rows.length === 0 && (
            <p className="text-sm text-slate-500">
              Upload a certificate above and its details appear here for you to check.
            </p>
          )}

          <CpdCertificateRows
            rows={rows}
            onChange={setRowField}
            onRemove={rows.length > 1 ? removeRow : undefined}
            attempted={attempted}
            disabled={saving}
            categoryOptions={categoryOptions}
          />
        </FormSection>

        <FormSection step={3} title="Remarks">
          <Field label="Remarks" hint="Applied to every declaration created from this upload.">
            <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowCpdNew;
